import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import styled from 'styled-components';
import Select from '@mui/material/Select';
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PatientRecordSummary from '../../types/PatientRecordSummary';

const StyledSelect = styled(Select)(({ theme }) => `
    && {
      background-color: ${theme.palette.background.paper};
      font-family: Poppins, sans-serif;
      font-weight: 400;
      font-size: 14px;
      width: 100%;
      color: ${theme.palette.grey[900]};
      & .select-placeholder {
        font-size: 14px;
        color: ${theme.palette.grey[500]};
      }
         
      &.MuiOutlinedInput-root {
        & .MuiOutlinedInput-notchedOutline {
          border-color: ${theme.palette.grey[500]};
          border-radius: 4px;
        }
        &:hover .MuiOutlinedInput-notchedOutline {
          border-color: ${theme.palette.grey[500]};
        }
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
          border-color: ${theme.palette.grey[500]};
          border-width: 1px;
        }
      }
      
      & .MuiSelect-icon {
        font-size: 33px;
        right: 4px;
      }
    }
`);

const StyledMenuItem = styled(MenuItem)(({ theme }) => `
  && {
    font-family: Poppins, sans-serif;
    font-size: 14px;
    min-height: 40px;
    font-weight: 400;
    &.Mui-selected {
      background-color: ${theme.palette.grey[200]};
      &:hover {
        background-color: ${theme.palette.grey[200]};
      }
      &:disabled, &.Mui-disabled, &:disabled:hover {
        background-color: ${theme.palette.action.disabledBackground};
        cursor: not-allowed;
      }
    }
  }
`);

interface PatientRecordSelectProps {
  patientRecordSummaries?: Array<PatientRecordSummary>;
  selectedPatientRecord?: PatientRecordSummary;
  setSelectedPatientRecord: (record?: PatientRecordSummary) => void;
}

export default function PatientRecordSelect({ patientRecordSummaries, selectedPatientRecord, setSelectedPatientRecord }: Readonly<PatientRecordSelectProps>) {
  const { t } = useTranslation();
  return (
    <StyledSelect
      size="small"
      displayEmpty
      data-testid="record-mapping-dropdown"
      value={selectedPatientRecord?.multiRecordUid || ''}
      onChange={(e) => setSelectedPatientRecord(patientRecordSummaries?.find((record) => record.multiRecordUid === e.target.value))}
      IconComponent={ArrowDropDownRoundedIcon}
      renderValue={(selected: any) => {
        if (!selected) {
          return <span className="select-placeholder">{t('Traits.Record Mapping Placeholders.Update')}</span>;
        }
        return patientRecordSummaries?.find((record) => record.multiRecordUid === selected)?.recordSummaryText;
      }}
    >
      {patientRecordSummaries?.map(({ recordSummaryText, multiRecordUid, inUse }) => (
        <StyledMenuItem key={multiRecordUid} value={multiRecordUid} data-testid={`record-option-${multiRecordUid}`} disabled={inUse}>
          {`${recordSummaryText} ${inUse ? t('Traits.PatientRecord.InUse') : ''}`}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );
}
